/* eslint-disable react/react-in-jsx-scope */
// import { Image, Table } from "react-bootstrap";
import React from "react";
import { Card, Image } from "react-bootstrap";
import logo from "../../../assets/img/KTSG.png";
import { formatNumber } from "../../../redux/utils";
import { toWordsconver } from "../../../redux/api";
import "./style.css";

const Trailbalance = ({
  form = {},
  user = {},
  data = [],
  taxesApplied = [],
  isMultiplePVs = false,
  showTaxSummary = false,
  isTaxPV = false,
  isPreview = false, // New prop to indicate preview mode
}) => {
  const capitalizeFirstLetter = (string) => {
    if (!string) return "";
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  // Helper function to get current date in YYYY-MM-DD format
  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Helper function to get display date for preview
  const getDisplayDate = (item) => {
    if (isPreview) {
      // In preview mode, use form date or current date
      return form.date || getCurrentDate();
    }
    return item?.pv_date || item?.date;
  };

  // Helper function to get PV code for display
  const getPvCode = (item) => {
    if (isPreview) {
      // In preview mode, return empty string (will show blank boxes)
      return "";
    }
    return item?.pv_code || "";
  };

  // Helper function to create tax breakdown description
  const createTaxBreakdownDescription = (originalDescription, taxes, netAmount) => {
    if (!taxes || taxes.length === 0) return originalDescription;
    
    let description = originalDescription || "Payment for services rendered";
    description += "\n\n=== TAX BREAKDOWN ===";
    
    const grossAmount = parseFloat(form?.amount || form?.approved_amount || 0);
    description += `\nGross Amount: ₦${grossAmount.toLocaleString()}`;
    
    taxes.forEach((tax, index) => {
      const rate = tax.rate ? ` (${tax.rate}%)` : '';
      description += `\n${index + 1}. ${tax.description}${rate}: ₦${parseFloat(tax.amount || 0).toLocaleString()}`;
    });
    
    const totalTaxes = taxes.reduce((sum, tax) => sum + parseFloat(tax.amount || 0), 0);
    description += `\n\nTotal Tax Deductions: ₦${totalTaxes.toLocaleString()}`;
    description += `\nNet Amount Payable: ₦${parseFloat(netAmount || 0).toLocaleString()}`;
    description += `\n\nNote: Separate PVs will be generated for each tax deduction.`;
    
    return description;
  };

  // Generate preview PVs for capital projects with taxes
  const generatePreviewPVs = () => {
    if (!isPreview || !taxesApplied || taxesApplied.length === 0) {
      return data && data.length > 0 ? data : [form];
    }

    const grossAmount = parseFloat(form?.amount || form?.approved_amount || 0);
    const totalTaxAmount = taxesApplied.reduce((sum, tax) => sum + parseFloat(tax.amount || 0), 0);
    const netAmount = grossAmount - totalTaxAmount;

    // Main PV with tax breakdown
    const mainPV = {
      ...form,
      pv_type: 'main',
      original_amount: grossAmount,
      net_amount: netAmount,
      amount: netAmount,
      tax_details: taxesApplied,
      narration: createTaxBreakdownDescription(
        form?.narration || form?.description_code || "Payment for services rendered",
        taxesApplied,
        netAmount
      ),
      description: createTaxBreakdownDescription(
        form?.narration || form?.description_code || "Payment for services rendered", 
        taxesApplied,
        netAmount
      )
    };

    // Individual tax PVs
    const taxPVs = taxesApplied.map((tax, index) => ({
      ...form,
      pv_code: `PREVIEW-TAX-${index + 1}`,
      pv_type: 'tax',
      tax_name: tax.description,
      tax_amount: tax.amount,
      amount: tax.amount,
      narration: `${tax.description}`,
      description: `${tax.description}`,
      parent_pv_code: 'PREVIEW-MAIN',
      tax_details: [tax]
    }));

    return [mainPV, ...taxPVs];
  };

  // Handle preview mode where data might be empty
  const displayData = generatePreviewPVs();

  // Calculate amounts properly for preview mode
  let totalAmount = 0;
  if (isPreview) {
    totalAmount = parseFloat(form?.amount || form?.approved_amount || 0);
  } else {
    totalAmount = displayData.reduce((a, b) => a + parseFloat(b.amount || 0), 0);
  }

  let totalTaxesAmount = taxesApplied.reduce(
    (a, b) => a + parseFloat(b.amount || 0),
    0
  );

  // For multiple PVs, use the net amount directly from the PV data
  let netAmount = isMultiplePVs && displayData[0]?.net_amount !== undefined 
    ? parseFloat(displayData[0].net_amount)
    : parseFloat(totalAmount) - parseFloat(totalTaxesAmount);

  if (!isPreview) {
    displayData.forEach((item) => {
      totalAmount += parseInt(item.amount || 0);
    });

    if (!isMultiplePVs) {
      taxesApplied.forEach((tax) => {
        totalAmount -= parseInt(tax.amount || 0);
      });
    }
  }

  return (
    <div style={{ height: '100vh', overflow: 'scroll'}}>
      <div>
        {displayData.map((item, index) => {
          const pendingSignature = item.history
            ? item.history.find((a) => a.pv_status === "Pending")
            : null;
          const redivedSignature = item.history
            ? item.history.find((a) => a.pv_status === "Reviewed")
            : null;
          const checkedSignature = item.history
            ? item.history.find((a) => a.pv_status === "Checked")
            : null;
          const approvedSignature = item.history
            ? item.history.find((a) => a.pv_status === "Approved")
            : null;

          // Determine PV type for preview
          const pvType = item.pv_type || 'main';
          const isMainPV = pvType === 'main' || index === 0;
          const isTaxPV = pvType === 'tax';

          return (
            <Card style={{marginBottom: 20}}
              key={index}
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                pageBreakBefore: index > 0 ? "always" : "auto",
                marginBottom: index < displayData.length - 1 ? "50px" : "0"
              }}
            >
              {/* Preview header for multiple PVs */}
              {isPreview && displayData.length > 1 && (
                <div
                  style={{
                    width: "100%",
                    textAlign: "center",
                    padding: "15px",
                    backgroundColor: isTaxPV ? "#fff3cd" : "#d1ecf1",
                    border: `2px solid ${isTaxPV ? "#ffc107" : "#bee5eb"}`,
                    marginBottom: "20px",
                    borderRadius: "8px",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                  }}
                >
                  <h4 style={{ 
                    margin: 0, 
                    color: isTaxPV ? "#856404" : "#0c5460",
                    fontSize: "18px",
                    fontWeight: "bold"
                  }}>
                    {isTaxPV 
                      ? `TAX DEDUCTION PV ${index} - ${item.tax_name}` 
                      : "MAIN PAYMENT VOUCHER (Net Amount After Tax Deductions)"
                    }
                  </h4>
                  {isTaxPV && (
                    <div style={{ 
                      margin: "8px 0 0 0", 
                      fontSize: "14px",
                      color: "#856404"
                    }}>
                      <strong>Tax Amount: ₦{parseFloat(item.tax_amount || item.amount || 0).toLocaleString()}</strong>
                      <br />
                      <small>This PV represents the tax deduction for {item.tax_name}</small>
                    </div>
                  )}
                  {isMainPV && taxesApplied.length > 0 && (
                    <div style={{ 
                      margin: "8px 0 0 0", 
                      fontSize: "14px",
                      color: "#0c5460"
                    }}>
                      <div style={{ marginBottom: "5px" }}>
                        <strong>Gross Amount:</strong> ₦{parseFloat(item.original_amount || 0).toLocaleString()} | 
                        <strong> Total Taxes:</strong> ₦{taxesApplied.reduce((sum, tax) => sum + parseFloat(tax.amount || 0), 0).toLocaleString()} | 
                        <strong> Net Payable:</strong> ₦{parseFloat(item.net_amount || item.amount || 0).toLocaleString()}
                      </div>
                      <small>This is the main PV showing net amount after all tax deductions</small>
                    </div>
                  )}
                  <div style={{
                    marginTop: "10px",
                    fontSize: "12px",
                    color: "#666",
                    fontStyle: "italic"
                  }}>
                    PV {index + 1} of {displayData.length} • Preview Mode
                  </div>
                </div>
              )}
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  margin: "auto",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Image
                  src={logo}
                  alt="logo"
                  style={{
                    width: 110,
                    height: 100,
                    marginLeft: -15,
                    marginTop: 20,
                  }}
                />
                <p
                  style={{
                    fontSize: 17,
                    fontWeight: "bold",
                    marginBottom: 0,
                    marginTop: 0,
                  }}
                >
                  KATSINA STATE GOVERNMENT
                </p>
                <p
                  style={{
                    fontSize: 20,
                    fontWeight: "bold",
                    marginBottom: 0,
                    marginTop: 0,
                  }}
                >
                  MINISTRY OF FINANCE
                </p>
                <p
                  style={{
                    fontSize: 17,
                    fontWeight: "bold",
                    marginBottom: 0,
                    marginTop: 0,
                  }}
                >
                  MINISTERIAL/DEPARTMENTAL PAYMENT VOUCHER
                </p>
              </div>
              <div
                style={{
                  width: "100%",
                  paddingLeft: 20,
                  paddingRight: 20,
                  fontSize: 10,
                  display: "flex",
                  flexDirection: "row",
                  lineHeight: 1.5,
                }}
              >
                <div
                  style={{
                    width: "75%",
                    height: "76vh",
                    borderRight: "1px solid #000",
                  }}
                >
                  <p
                    style={{
                      fontSize: 18,
                      fontWeight: "500",
                      width: "100%",
                      marginLeft: 300,
                      marginBottom: 0,
                      marginTop: 0,
                    }}
                  >
                    PAYMENT VOUCHER
                  </p>
                  <p
                    style={{
                      fontWeight: "bold",
                      fontSize: 12,
                      marginBottom: 0,
                      marginTop: 0,
                    }}
                  >
                    KTT.44
                  </p>
                  <p
                    style={{
                      fontSize: 12,
                      marginBottom: 0,
                      marginTop: 0,
                    }}
                  >
                    Organisation:{" "}
                    {item?.mda_name || form?.mda_name || user?.mda_name
                      ? item?.mda_name || form?.mda_name || user?.mda_name
                      : "......................................................................................................"}
                  </p>
                  <p
                    style={{
                      fontSize: 12,
                      marginBottom: 0,
                      marginTop: 0,
                    }}
                  >
                    Sub Organisation Name:{" "}
                    {item?.sub || form?.sub
                      ? item?.sub || form?.sub
                      : "......................................................................................................"}
                  </p>
                  <p
                    style={{
                      fontSize: 12,
                      marginBottom: 0,
                      marginTop: 0,
                    }}
                  >
                    Sub Sub Organisation Name:{" "}
                    {item?.sub_sub || form?.sub_sub
                      ? item?.sub_sub || form?.sub_sub
                      : "......................................................................................................"}
                  </p>
                  <p
                    style={{
                      fontSize: 12,
                      marginBottom: 0,
                      marginTop: 0,
                    }}
                  >
                    Sub Sub Sub Organisation Name:{" "}
                    {item?.sub_sub_sub || form?.sub_sub_sub
                      ? item?.sub_sub_sub || form?.sub_sub_sub
                      : "......................................................................................................"}
                  </p>
                  <p
                    style={{
                      fontSize: 12,
                      marginBottom: 0,
                      marginTop: 0,
                    }}
                  >
                    Payment to:{" "}
                    {item?.payment_to || form?.payment_to
                      ? item?.payment_to || form?.payment_to
                      : "................................................................................................................."}
                  </p>
                  <div>
                    <table className="invoice-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Detailed description of service</th>
                          <th>Rate</th>
                          <th colSpan="2">Amount(₦)</th>
                        </tr>
                        <tr style={{ width: "100%" }}>
                          <th></th>
                          <th></th>
                          <th></th>
                          <th style={{ width: "15%" }}>₦</th>
                          <th style={{ width: "5%" }}>K</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Main service description row */}
                        <tr style={{ width: "100%" }}>
                          <td style={{ verticalAlign: "top", fontSize: "10px", padding: "5px" }}>
                            {getDisplayDate(item) ? new Date(getDisplayDate(item)).toLocaleDateString() : new Date().toLocaleDateString()}
                          </td>
                          <td
                            style={{
                              textAlign: "left",
                              verticalAlign: "top",
                              fontSize: "10px",
                              padding: "5px"
                            }}
                          >
                            {capitalizeFirstLetter(item?.narration || item?.description || form?.narration || form?.description_code || "Payment for services rendered")}
                          </td>
                          <td style={{ verticalAlign: "top", fontSize: "10px", padding: "5px" }}></td>
                          <td style={{ textAlign: "right", verticalAlign: "top", fontSize: "10px", padding: "5px" }}>
                            {isTaxPV ? 
                              formatNumber(item?.tax_amount || item?.amount)?.split(".")[0] :
                              isMultiplePVs && item?.original_amount ? 
                                formatNumber(item.original_amount)?.split(".")[0] :
                                formatNumber(item?.amount || form?.amount || form?.approved_amount)?.split(".")[0]
                            }
                          </td>
                          <td style={{ verticalAlign: "top", fontSize: "10px", padding: "5px" }}>
                            {isTaxPV ? 
                              (item?.tax_amount || item?.amount)?.toString()?.split(".")[1] || "00" :
                              isMultiplePVs && item?.original_amount ? 
                                item.original_amount?.toString()?.split(".")[1] || "00" :
                                (item?.amount || form?.amount || form?.approved_amount)?.toString()?.split(".")[1] || "00"
                            }
                          </td>
                        </tr>
                        
                        {/* Tax deduction rows - show when we have taxes and it's the main PV */}
                        {(isMultiplePVs || (isPreview && isMainPV)) && showTaxSummary && item?.tax_details && item.tax_details.length > 0 && 
                          item.tax_details.map((tax, idx) => (
                            <tr key={idx} style={{ width: "100%", borderTop: "1px solid #ddd", backgroundColor: "#f8f9fa" }}>
                              <td style={{ verticalAlign: "top", fontSize: "10px", padding: "5px" }}>
                                 {getDisplayDate(item) ? new Date(getDisplayDate(item)).toLocaleDateString() : new Date().toLocaleDateString()}
                              </td>
                              <td
                                style={{
                                  textAlign: "left",
                                  verticalAlign: "top",
                                  fontSize: "10px",
                                  padding: "5px",
                                  fontStyle: "italic",
                                  color: "#d32f2f"
                                }}
                              >
                                Less: {tax.description} {tax.rate ? `(${tax.rate}%)` : ''} - Deduction
                                {isPreview && (
                                  <div style={{ fontSize: "9px", color: "#666", marginTop: "2px" }}>
                                    (Separate tax PV will be generated)
                                  </div>
                                )}
                              </td>
                              <td style={{ verticalAlign: "top", fontSize: "10px", padding: "5px" }}>
                                {tax.rate ? `${tax.rate}%` : ""}
                              </td>
                              <td style={{ textAlign: "right", verticalAlign: "top", fontSize: "10px", padding: "5px", color: "#d32f2f" }}>
                                ({formatNumber(tax.amount)?.split(".")[0]})
                              </td>
                              <td style={{ verticalAlign: "top", fontSize: "10px", padding: "5px", color: "#d32f2f" }}>
                                {tax.amount?.toString()?.split(".")[1] || "00"}
                              </td>
                            </tr>
                          ))
                        }
                        
                        {/* Net amount row for multiple PVs or main PV with taxes */}
                        {((isMultiplePVs || (isPreview && isMainPV)) && showTaxSummary && item?.tax_details && item.tax_details.length > 0) && (
                          <tr style={{ width: "100%", borderTop: "3px double #000", fontWeight: "bold", backgroundColor: "#e8f5e8" }}>
                            <td style={{ verticalAlign: "top", fontSize: "10px", padding: "8px" }}></td>
                            <td
                              style={{
                                textAlign: "left",
                                verticalAlign: "top",
                                fontSize: "11px",
                                padding: "8px",
                                fontWeight: "bold",
                                color: "#2e7d32"
                              }}
                            >
                              NET AMOUNT PAYABLE (After Tax Deductions)
                              {isPreview && (
                                <div style={{ fontSize: "9px", color: "#666", marginTop: "2px", fontWeight: "normal" }}>
                                  This is the final amount to be paid to the contractor
                                </div>
                              )}
                            </td>
                            <td style={{ verticalAlign: "top", fontSize: "10px", padding: "8px" }}></td>
                            <td style={{ textAlign: "right", verticalAlign: "top", fontSize: "11px", padding: "8px", fontWeight: "bold", color: "#2e7d32" }}>
                              {formatNumber(item.net_amount || item.amount || form?.amount || form?.approved_amount || netAmount)?.split(".")[0]}
                            </td>
                            <td style={{ verticalAlign: "top", fontSize: "11px", padding: "8px", fontWeight: "bold", color: "#2e7d32" }}>
                              {(item.net_amount || item.amount || form?.amount || form?.approved_amount || netAmount)?.toString()?.split(".")[1] || "00"}
                            </td>
                          </tr>
                        )}
                        
                        {/* Spacer rows to maintain table height */}
                        {Array.from({ 
                          length: Math.max(1, 8 - (
                            ((isMultiplePVs || (isPreview && isMainPV)) && item?.tax_details ? item.tax_details.length + 2 : 1)
                          )) 
                        }, (_, idx) => (
                          <tr key={`spacer-${idx}`} style={{ height: "2vh" }}>
                            <td colSpan="5" style={{ borderBottom: "none" }}></td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ width: "100%", height: "4vh" }}>
                          <td colSpan="2" style={{ textAlign: "justify" }}>
                            TOTAL AMOUNT IN WORDS:
                            {toWordsconver(
                              (isTaxPV ? 
                                (item?.tax_amount || item?.amount || 0) :
                                isPreview ? 
                                  (item?.net_amount || item?.amount || form?.amount || form?.approved_amount || 0) : 
                                  (isMultiplePVs && item?.net_amount ? item.net_amount : netAmount)
                              ).toString().split(".")[0]
                            )?.toUpperCase()}
                            NAIRA
                            {(isTaxPV ? 
                                (item?.tax_amount || item?.amount || 0) :
                                isPreview ? 
                                  (item?.net_amount || item?.amount || form?.amount || form?.approved_amount || 0) : 
                                  (isMultiplePVs && item?.net_amount ? item.net_amount : netAmount)
                              ).toString().split(".")[1] &&
                            (isTaxPV ? 
                                (item?.tax_amount || item?.amount || 0) :
                                isPreview ? 
                                  (item?.net_amount || item?.amount || form?.amount || form?.approved_amount || 0) : 
                                  (isMultiplePVs && item?.net_amount ? item.net_amount : netAmount)
                              ).toString().split(".")[1] !== "00"
                              ? ` AND ${toWordsconver(
                                  (isTaxPV ? 
                                    (item?.tax_amount || item?.amount || 0) :
                                    isPreview ? 
                                      (item?.net_amount || item?.amount || form?.amount || form?.approved_amount || 0) : 
                                      (isMultiplePVs && item?.net_amount ? item.net_amount : netAmount)
                                  ).toString().split(".")[1]
                                )?.toUpperCase()} KOBO`
                              : null}{" "}
                            ONLY
                          </td>
                          <td colSpan="2" style={{ textAlign: "right" }}>
                            {formatNumber(
                              isTaxPV ? 
                                (item?.tax_amount || item?.amount || 0) :
                                isPreview ? 
                                  (item?.net_amount || item?.amount || form?.amount || form?.approved_amount || 0) : 
                                  (isMultiplePVs && item?.net_amount ? item.net_amount : netAmount)
                            )?.split(".")[0]}
                          </td>
                          <td>
                            {(isTaxPV ? 
                                (item?.tax_amount || item?.amount || 0) :
                                isPreview ? 
                                  (item?.net_amount || item?.amount || form?.amount || form?.approved_amount || 0) : 
                                  (isMultiplePVs && item?.net_amount ? item.net_amount : netAmount)
                              )?.toString()?.split(".")[1] || "00"}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  
                  {/* Signature sections - simplified for preview */}
                  <div
                    style={{
                      width: "100%",
                      borderBottom: "1px solid #000",
                      paddingLeft: 2,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <p
                      style={{
                        width: "100%",
                        fontSize: 12,
                        marginBottom: 0,
                        marginTop: 0,
                      }}
                    >
                      We certify that the details above are in accordance with
                      the relevant contract, regulations or other authority
                      under which the Services/Goods were provided/purchased
                    </p>
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "row",
                      }}
                    >
                      <p
                        style={{
                          width: "70%",
                          fontSize: 12,
                          marginBottom: 0,
                          marginTop: 0,
                        }}
                      >
                        Officer who prepared voucher, Signature:
                      </p>
                      {!isPreview && pendingSignature?.signature ? (
                        <Image
                          style={{
                            width: 30,
                            height: 30,
                          }}
                          src={pendingSignature.signature}
                        />
                      ) : null}
                    </div>
                    <p
                      style={{
                        marginLeft: 53,
                        textTransform: "uppercase",
                        fontSize: 12,
                        marginBottom: 0,
                        marginTop: 0,
                      }}
                    >
                      Name (in block letters):
                      {!isPreview ? pendingSignature?.fullname : ""}
                    </p>
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "row",
                      }}
                    >
                      <p
                        style={{
                          width: "70%",
                          fontSize: 12,
                          marginBottom: 0,
                          marginTop: 0,
                        }}
                      >
                        Officer who checked voucher, Signature:
                      </p>
                      {!isPreview && checkedSignature?.signature ? (
                        <Image
                          style={{
                            width: 30,
                            height: 30,
                          }}
                          src={checkedSignature.signature}
                        />
                      ) : null}
                    </div>
                    <p
                      style={{
                        marginLeft: 53,
                        textTransform: "uppercase",
                        fontSize: 12,
                        marginBottom: 0,
                        marginTop: 0,
                      }}
                    >
                      Name (in block letters):{!isPreview ? checkedSignature?.fullname : ""}
                    </p>
                  </div>
                  
                  {/* Additional signature sections */}
                  <div
                    style={{
                      width: "100%",
                      borderBottom: "1px solid #000",
                      paddingLeft: 2,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <p
                      style={{
                        width: "100%",
                        fontSize: 12,
                        marginBottom: 0,
                        marginTop: 0,
                      }}
                    >
                      We certify that the Service/Goods have been duly
                      performed/received, that financial authority
                      GW/.............. is held to incur this expenditure and
                      that the relevant D V.E account entries have been made. We
                      hereby authorized payment.
                    </p>
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <p
                        style={{
                          width: "30%",
                          fontSize: 12,
                          marginBottom: 0,
                          marginTop: 0,
                        }}
                      >
                        Signature:
                      </p>
                      {!isPreview && approvedSignature?.signature ? (
                        <Image
                          style={{
                            width: 30,
                            height: 30,
                          }}
                          src={approvedSignature.signature}
                        />
                      ) : null}
                    </div>
                    <p
                      style={{
                        width: "100%",
                        fontSize: 12,
                        marginBottom: 0,
                        marginTop: 0,
                      }}
                    >
                      (Accounting Officer)
                    </p>
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "row",
                        marginBottom: 20,
                      }}
                    >
                      <p
                        style={{
                          width: "50%",
                          fontSize: 12,
                          marginBottom: 0,
                          marginTop: 0,
                          textTransform: "uppercase",
                        }}
                      >
                        Name (in block letters):
                        {!isPreview ? approvedSignature?.fullname : ""}
                      </p>
                      <p
                        style={{
                          width: "50%",
                          fontSize: 12,
                          marginBottom: 0,
                          marginTop: 0,
                        }}
                      >
                        Date:
                        {getDisplayDate(item) || getCurrentDate()}
                      </p>
                    </div>
                  </div>
                  
                  {/* Bank details section */}
                  <div
                    style={{
                      width: "100%",
                      borderBottom: "1px solid #000",
                      borderTop: "1px solid #000",
                      paddingLeft: 2,
                      display: "flex",
                      flexDirection: "row",
                    }}
                  >
                    <p
                      style={{
                        width: "50%",
                        fontSize: 12,
                        marginBottom: 0,
                        marginTop: 0,
                      }}
                    >
                      Cheque No:
                    </p>
                    <p
                      style={{
                        width: "50%",
                        fontSize: 12,
                        marginBottom: 0,
                        marginTop: 0,
                      }}
                    >
                      Account No:{" "}
                      {item?.contractor_acc_no || item?.mda_account_no || form?.mda_account_no || ""}
                    </p>
                  </div>
                  <div
                    style={{
                      width: "100%",
                      borderBottom: 1,
                      paddingLeft: 2,
                      display: "flex",
                      flexDirection: "row",
                    }}
                  >
                    <p
                      style={{
                        width: "50%",
                        fontSize: 12,
                        marginBottom: 0,
                        marginTop: 0,
                      }}
                    >
                      Account Name:{" "}
                      {item?.contractor_acc_name || item?.mda_account_name || form?.mda_account_name || ""}
                    </p>
                    <p
                      style={{
                        width: "50%",
                        fontSize: 12,
                        marginBottom: 0,
                        marginTop: 0,
                      }}
                    >
                      Name of Bank:{" "}
                      {item?.contractor_bank_name || item?.mda_account_bank_name || form?.mda_account_bank_name || ""}
                    </p>
                  </div>
                  
                  {/* Receipt section */}
                  <div
                    style={{
                      width: "100%",
                      paddingLeft: 2,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <p
                      style={{
                        width: "100%",
                        fontSize: 12,
                        marginBottom: 0,
                        marginTop: 0,
                      }}
                    >
                      Received the sum of .....................................
                      Naira and......................................Kobo in
                    </p>
                    <p
                      style={{
                        width: "100%",
                        fontSize: 12,
                        marginBottom: 0,
                        marginTop: 0,
                      }}
                    >
                      acquisition of the above Account
                      this.............................Day
                      of..........................20..........
                    </p>
                    <p
                      style={{
                        width: "100%",
                        fontSize: 12,
                        marginBottom: 0,
                        marginTop: 0,
                      }}
                    >
                      Amount in word.....................................Name of
                      Payee...................................................
                    </p>
                  </div>
                </div>
                
                {/* Right side - Voucher Number and Date */}
                <div style={{ width: "30%", paddingLeft: 20, paddingTop: 10 }}>
                  <div
                    style={{
                      width: "100%",
                    }}
                  >
                    <p
                      style={{
                        width: "100%",
                        textAlign: "center",
                        fontSize: 12,
                        marginBottom: 0,
                        marginTop: 0,
                      }}
                    >
                      Voucher Number
                    </p>
                    <div
                      style={{
                        width: "100%",
                        border: "1px solid #000",
                        display: "flex",
                        flexDirection: "row",
                        height: 24,
                      }}
                    >
                      {isPreview ? (
                        // In preview mode, show empty boxes
                        Array.from({ length: 8 }, (_, index) => (
                          <p
                            key={index}
                            style={{
                              width: "12.5%",
                              height: 22,
                              textAlign: "center",
                              fontSize: 12,
                              marginBottom: 0,
                              marginTop: 0,
                              borderRight: index < 7 ? "1px solid #000" : 0,
                              backgroundColor: "#f8f9fa",
                            }}
                          >
                            &nbsp;
                          </p>
                        ))
                      ) : (
                        // Normal mode, show actual PV code
                        String(getPvCode(item) || "")
                          .split("")
                          .map((digit, index) => (
                            <p
                              key={index}
                              style={{
                                width: "12.5%",
                                height: 22,
                                textAlign: "center",
                                fontSize: 12,
                                marginBottom: 0,
                                marginTop: 0,
                                borderRight:
                                  index < String(getPvCode(item) || "").length - 1
                                    ? "1px solid #000"
                                    : 0,
                              }}
                            >
                              {digit}
                            </p>
                          ))
                      )}
                    </div>
                  </div>
                  
                  {/* Date section */}
                  <div
                    style={{
                      width: "100%",
                      marginTop: 30,
                    }}
                  >
                    <p
                      style={{
                        width: "100%",
                        textAlign: "center",
                        marginBottom: 0,
                        marginTop: 0,
                        fontSize: 12,
                      }}
                    >
                      Year/Month/Day
                    </p>
                    <div
                      style={{
                        width: "100%",
                        height: 24,
                        border: "1px solid #000",
                        display: "flex",
                        flexDirection: "row",
                      }}
                    >
                      {String(getDisplayDate(item) || getCurrentDate())
                        .split("")
                        .map((digit, index) => (
                          <p
                            key={index}
                            style={{
                              width: "10%",
                              height: 22,
                              textAlign: "center",
                              fontSize: 12,
                              marginBottom: 0,
                              marginTop: 0,
                              borderRight:
                                index < String(getDisplayDate(item) || getCurrentDate()).length - 1
                                  ? "1px solid #000"
                                  : 0,
                            }}
                          >
                            {digit}
                          </p>
                        ))}
                    </div>
                  </div>

                  {/* Administrative Code section */}
                  <div
                    style={{
                      width: "100%",
                      marginTop: 30,
                    }}
                  >
                    <p
                      style={{
                        width: "100%",
                        textAlign: "center",
                        marginBottom: 0,
                        marginTop: 0,
                        fontSize: 12,
                      }}
                    >
                      Administrative Code
                    </p>
                    <div
                      style={{
                        width: "100%",
                        border: "1px solid #000",
                        display: "flex",
                        flexDirection: "row",
                        height: 24,
                        textAlign: "center",
                      }}
                    >
                      {String(item?.mda_code || form?.mda_code || "")
                        .split("")
                        .map((digit, index) => (
                          <p
                            key={index}
                            style={{
                              width: "12.5%",
                              height: 22,
                              textAlign: "center",
                              fontSize: 12,
                              marginBottom: 0,
                              marginTop: 0,
                              borderRight:
                                index < String(item?.mda_code || form?.mda_code || "").length - 1
                                  ? "1px solid #000"
                                  : 0,
                            }}
                          >
                            {digit}
                          </p>
                        ))}
                    </div>
                  </div>

                  {/* Economic Code section */}
                  <div
                    style={{
                      width: "100%",
                      marginTop: 30,
                    }}
                  >
                    <p
                      style={{
                        width: "100%",
                        textAlign: "center",
                        marginBottom: 0,
                        marginTop: 0,
                        fontSize: 12,
                      }}
                    >
                      Economic Code
                    </p>
                    <div
                      style={{
                        width: "100%",
                        border: "1px solid #000",
                        display: "flex",
                        flexDirection: "row",
                        height: 24,
                        textAlign: "center",
                      }}
                    >
                      {String(item?.economic_code || form?.economic_code || form?.sourceEconomicCode || "")
                        .split("")
                        .map((digit, index) => (
                          <p
                            key={index}
                            style={{
                              width: "12.5%",
                              height: 22,
                              textAlign: "center",
                              fontSize: 12,
                              marginBottom: 0,
                              marginTop: 0,
                              borderRight:
                                index < String(item?.economic_code || form?.economic_code || form?.sourceEconomicCode || "").length - 1
                                  ? "1px solid #000"
                                  : 0,
                            }}
                          >
                            {digit}
                          </p>
                        ))}
                    </div>
                  </div>

                  {/* Program Code section */}
                  <div
                    style={{
                      width: "100%",
                      marginTop: 30,
                    }}
                  >
                    <p
                      style={{
                        width: "100%",
                        textAlign: "center",
                        marginBottom: 0,
                        marginTop: 0,
                        fontSize: 12,
                      }}
                    >
                      Program Code
                    </p>
                    <div
                      style={{
                        width: "100%",
                        border: "1px solid #000",
                        display: "flex",
                        flexDirection: "row",
                        height: 24,
                        textAlign: "center",
                      }}
                    >
                      {String(item?.program_code || form?.program_code || "")
                        .split("")
                        .map((digit, index) => (
                          <p
                            key={index}
                            style={{
                              width: "12.5%",
                              height: 22,
                              textAlign: "center",
                              fontSize: 12,
                              marginBottom: 0,
                              marginTop: 0,
                              borderRight:
                                index < String(item?.program_code || form?.program_code || "").length - 1
                                  ? "1px solid #000"
                                  : 0,
                            }}
                          >
                            {digit}
                          </p>
                        ))}
                    </div>
                  </div>

                  {/* Functional Code section */}
                  <div
                    style={{
                      width: "100%",
                      marginTop: 30,
                    }}
                  >
                    <p
                      style={{
                        width: "100%",
                        textAlign: "center",
                        marginBottom: 0,
                        marginTop: 0,
                        fontSize: 12,
                      }}
                    >
                      Functional Code
                    </p>
                    <div
                      style={{
                        width: "100%",
                        border: "1px solid #000",
                        display: "flex",
                        flexDirection: "row",
                        height: 24,
                        textAlign: "center",
                      }}
                    >
                      {String(item?.functional_code || form?.functional_code || "")
                        .split("")
                        .map((digit, index) => (
                          <p
                            key={index}
                            style={{
                              width: "12.5%",
                              height: 22,
                              textAlign: "center",
                              fontSize: 12,
                              marginBottom: 0,
                              marginTop: 0,
                              borderRight:
                                index < String(item?.functional_code || form?.functional_code || "").length - 1
                                  ? "1px solid #000"
                                  : 0,
                            }}
                          >
                            {digit}
                          </p>
                        ))}
                    </div>
                  </div>

                  {/* Fund Code section */}
                  <div
                    style={{
                      width: "100%",
                      marginTop: 30,
                    }}
                  >
                    <p
                      style={{
                        width: "100%",
                        textAlign: "center",
                        marginBottom: 0,
                        marginTop: 0,
                        fontSize: 12,
                      }}
                    >
                      Fund Code
                    </p>
                    <div
                      style={{
                        width: "100%",
                        border: "1px solid #000",
                        display: "flex",
                        flexDirection: "row",
                        height: 24,
                        textAlign: "center",
                      }}
                    >
                      {String(item?.fund_code || form?.fund_code || "")
                        .split("")
                        .map((digit, index) => (
                          <p
                            key={index}
                            style={{
                              width: "12.5%",
                              height: 22,
                              textAlign: "center",
                              fontSize: 12,
                              marginBottom: 0,
                              marginTop: 0,
                              borderRight:
                                index < String(item?.fund_code || form?.fund_code || "").length - 1
                                  ? "1px solid #000"
                                  : 0,
                            }}
                          >
                            {digit}
                          </p>
                        ))}
                    </div>
                  </div>

                  {/* Geo Code section */}
                  <div
                    style={{
                      width: "100%",
                      marginTop: 30,
                    }}
                  >
                    <p
                      style={{
                        width: "100%",
                        textAlign: "center",
                        marginBottom: 0,
                        marginTop: 0,
                        fontSize: 12,
                      }}
                    >
                      Geo Code
                    </p>
                    <div
                      style={{
                        width: "100%",
                        border: "1px solid #000",
                        display: "flex",
                        flexDirection: "row",
                        height: 24,
                        textAlign: "center",
                      }}
                    >
                      {String(item?.geo_code || form?.geo_code || "")
                        .split("")
                        .map((digit, index) => (
                          <p
                            key={index}
                            style={{
                              width: "12.5%",
                              height: 22,
                              textAlign: "center",
                              fontSize: 12,
                              marginBottom: 0,
                              marginTop: 0,
                              borderRight:
                                index < String(item?.geo_code || form?.geo_code || "").length - 1
                                  ? "1px solid #000"
                                  : 0,
                            }}
                          >
                            {digit}
                          </p>
                        ))}
                    </div>
                  </div>

                  {/* Additional sections for treasury operations */}
                  <div
                    style={{
                      width: "100%",
                      marginTop: 50,
                    }}
                  >
                    <p style={{ fontSize: 12, marginBottom: 0, marginTop: 0 }}>
                      Date received in Treasury{" "}
                    </p>
                    <p style={{ fontSize: 12, marginBottom: 0, marginTop: 0 }}>
                      ............................................
                    </p>
                    <p style={{ fontSize: 12, marginBottom: 0, marginTop: 0 }}>
                      Check and passed at{" "}
                    </p>
                    <p style={{ fontSize: 12, marginBottom: 0, marginTop: 0 }}>
                      ............................................
                    </p>
                    <p style={{ fontSize: 12, marginBottom: 0, marginTop: 0 }}>
                      Signature{" "}
                    </p>
                    <p style={{ fontSize: 12, marginBottom: 0, marginTop: 0 }}>
                      ............................................
                    </p>
                    <p style={{ fontSize: 12, marginBottom: 0, marginTop: 0 }}>
                      Rank........................................
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default Trailbalance;